import type { Ref } from 'vue'
import { computed, unref } from 'vue'
import type { PitcherEnv } from '@sdk/interfaces'

/**
 * Composable for SmartStore availability checks
 *
 * SmartStore is Salesforce Mobile SDK's local database for offline data access on iOS.
 * It requires both iOS mode and the enable_sfdc_sync instance setting to be enabled.
 *
 * @param env - Optional reactive environment object from app store (avoids redundant API calls)
 *
 * @example
 * // With app store env (recommended - avoids API calls)
 * const appStore = useAppStore()
 * const { shouldUseSmartStore } = useSmartStore(appStore.env)
 * const enabled = shouldUseSmartStore.value
 *
 * @example
 * // Without env (for canvas-ui internal usage)
 * const { generateTempSalesforceId } = useSmartStore()
 * const tempId = generateTempSalesforceId('00U')
 */
export function useSmartStore(env?: Ref<PitcherEnv | null> | PitcherEnv | null) {
  /**
   * Check if SmartStore should be used (iOS + enable_sfdc_sync instance setting)
   *
   * @returns boolean - true if SmartStore is available and should be used
   */
  const shouldUseSmartStore = computed<boolean>(() => {
    try {
      const envValue = unref(env)
      if (!envValue) {
        return false
      }
      const isIos = envValue?.mode === 'IOS'
      const isSfdcSyncEnabled = (envValue as any)?.pitcher?.instance?.settings?.enable_sfdc_sync === true

      return isIos && isSfdcSyncEnabled
    } catch (error) {
      console.error('[useSmartStore] Error checking SmartStore availability:', error)
      return false
    }
  })

  /**
   * Generate a temporary Salesforce-like ID for offline record creation
   *
   * @param objectPrefix - The Salesforce object prefix (e.g., '00U' for Event, 'a1R' for Call2_vod__c)
   * @returns string - A temporary 18-character Salesforce-style ID
   *
   * @example
   * const eventId = generateTempSalesforceId('00U')
   * // Returns: 'PIT_00U00000001XXX'
   */
  const generateTempSalesforceId = (objectPrefix: string): string => {
    // Generate a unique ID using timestamp and random string
    const timestamp = Date.now().toString(36)
    const randomStr = Math.random().toString(36).substring(2, 11)
    const uniqueId = (timestamp + randomStr).substring(0, 14 - objectPrefix.length)

    // Salesforce IDs are 18 characters: 'PIT_' (4 chars) + object prefix + unique ID (14 - prefix.length)
    return 'PIT_' + objectPrefix + uniqueId.padEnd(14 - objectPrefix.length, '0')
  }

  return {
    shouldUseSmartStore,
    generateTempSalesforceId,
  }
}
